/**
 * HeatmapCellDrawer — 개요 탭 요일×시간 결제 히트맵의 셀 드릴다운 드로어.
 *
 * 셀(요일 dow × 시간 hour)을 클릭하면, 이미 내려와 있는 rows[]를 서버 산출
 * dow/hour(KST) 로 필터해 해당 버킷의 개별 결제 목록을 보여준다. 새 API 호출 없음.
 */

import { useMemo } from 'react'
import {
  Drawer,
  Table,
  formatCurrency,
  formatDate,
  formatNumber,
  type TableColumn,
} from '@/ui'
import { CHANNEL_LABELS, PRODUCT_TYPE_LABELS, WEEKDAY_LABELS } from './labels'
import type { AttributionRow } from './types'
import styles from './HeatmapCellDrawer.module.css'

interface HeatmapCellDrawerProps {
  /** 선택된 셀 — null이면 닫힘 */
  cell: { dow: number; hour: number } | null
  rows: AttributionRow[]
  onClose: () => void
}

const columns: TableColumn<AttributionRow>[] = [
  {
    key: 'paidAt',
    label: '결제 시각',
    render: (r) => formatDate(r.paidAt, 'M.d HH:mm'),
  },
  {
    key: 'channel',
    label: '채널',
    render: (r) => CHANNEL_LABELS[r.channel] ?? r.channel,
  },
  {
    key: 'type',
    label: '상품',
    render: (r) => PRODUCT_TYPE_LABELS[r.type] ?? r.type,
  },
  {
    key: 'userName',
    label: '고객',
    render: (r) => r.userName ?? '(유입미상)',
  },
  {
    key: 'netAmount',
    label: '순매출',
    align: 'right',
    render: (r) => formatCurrency(r.netAmount),
  },
]

export function HeatmapCellDrawer({ cell, rows, onClose }: HeatmapCellDrawerProps) {
  // 선택된 버킷의 결제만 필터 (서버 KST dow/hour 기준 — 히트맵 셀 건수와 정확히 일치)
  const cellRows = useMemo(() => {
    if (!cell) return []
    return rows
      .filter((r) => r.dow === cell.dow && r.hour === cell.hour)
      .sort((a, b) => a.paidAt.localeCompare(b.paidAt))
  }, [cell, rows])

  const totalRevenue = useMemo(
    () => cellRows.reduce((acc, r) => acc + r.netAmount, 0),
    [cellRows],
  )

  const title =
    cell != null
      ? `${WEEKDAY_LABELS[cell.dow]}요일 ${cell.hour}시 결제`
      : '결제 상세'

  return (
    <Drawer open={cell != null} onClose={onClose} title={title} size="md">
      <div className={styles.summary}>
        <span>결제 {formatNumber(cellRows.length)}건</span>
        <span>순매출 {formatCurrency(totalRevenue)}</span>
      </div>
      <Table<AttributionRow>
        columns={columns}
        data={cellRows}
        size="sm"
        striped
        rowKey={(r) => r.paymentId}
        emptyMessage="이 시간대에 결제가 없습니다."
      />
    </Drawer>
  )
}
